import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  IconButton,
  Alert,
} from '@mui/material'
import {
  Schedule as ScheduleIcon,
  HealthAndSafety as HealthIcon,
  Email as EmailIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import ReminderService from '../../services/reminderService'
import type {
  ReminderStatusResponse,
  ReminderStatsResponse,
  ReminderHealthResponse,
} from '../../types/reminder'

export const ReminderStats: React.FC = () => {
  const { t, i18n } = useTranslation('dashboard')
  const [status, setStatus] = useState<ReminderStatusResponse | null>(null)
  const [stats, setStats] = useState<ReminderStatsResponse | null>(null)
  const [health, setHealth] = useState<ReminderHealthResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      const [statusData, statsData, healthData] = await Promise.all([
        ReminderService.getStatus(),
        ReminderService.getStats(),
        ReminderService.getHealth(),
      ])

      setStatus(statusData)
      setStats(statsData)
      setHealth(healthData)
    } catch (error: unknown) {
      console.error('Failed to fetch reminder data:', error)
      const errorMessage = error instanceof Error ? error.message : t('reminders.fetchError')
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  if (loading && !status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )
  }

  const healthInfo = health ? ReminderService.getStatusDisplayInfo(health) : null

  const getHealthText = () => {
    if (!healthInfo) return t('reminders.unknown')
    // Map the health text to translation keys
    if (healthInfo.text === '正常') return i18n.language === 'ja' ? '正常' : 'Healthy'
    if (healthInfo.text === '警告') return i18n.language === 'ja' ? '警告' : 'Warning'
    if (healthInfo.text === 'エラー') return i18n.language === 'ja' ? 'エラー' : 'Error'
    return healthInfo.text
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{t('reminders.systemTitle')}</Typography>
        <IconButton onClick={fetchData} disabled={loading} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3,
        }}
      >
        {/* システム状態 */}
        <Card>
          <CardHeader
            title={t('reminders.systemStatus')}
            avatar={
              <HealthIcon
                color={healthInfo?.color === 'default' ? 'inherit' : healthInfo?.color || 'inherit'}
              />
            }
            action={
              <Chip
                label={getHealthText()}
                color={healthInfo?.color || 'default'}
                variant="outlined"
                size="small"
              />
            }
            titleTypographyProps={{ variant: 'subtitle1' }}
          />
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">{t('reminders.autoReminder')}</Typography>
                <Chip
                  label={
                    status?.scheduler.enabled ? t('reminders.enabled') : t('reminders.disabled')
                  }
                  color={status?.scheduler.enabled ? 'primary' : 'default'}
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">{t('reminders.emailConfig')}</Typography>
                <Chip
                  label={
                    status?.email.configured
                      ? t('reminders.configured')
                      : t('reminders.notConfigured')
                  }
                  color={status?.email.configured ? 'primary' : 'secondary'}
                  size="small"
                  icon={<EmailIcon />}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">{t('reminders.database')}</Typography>
                <Chip
                  label={
                    status?.systemHealth.database
                      ? t('reminders.connected')
                      : t('reminders.disconnected')
                  }
                  color={status?.systemHealth.database ? 'primary' : 'error'}
                  size="small"
                  icon={<StorageIcon />}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* 統計情報 */}
        <Card>
          <CardHeader
            title={t('reminders.enrollmentStats')}
            titleTypographyProps={{ variant: 'subtitle1' }}
          />
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{t('reminders.activeEnrollments')}</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {stats?.statistics.totalActiveEnrollments || 0}
                  {t('reminders.items')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{t('reminders.dueSoon7Days')}</Typography>
                <Typography variant="body1" fontWeight="bold" color="secondary.main">
                  {stats?.statistics.upcomingDeadlinesCount || 0}
                  {t('reminders.items')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{t('reminders.overdueCount')}</Typography>
                <Typography variant="body1" fontWeight="bold" color="error.main">
                  {stats?.statistics.overdueCount || 0}
                  {t('reminders.items')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{t('reminders.recentCompletions')}</Typography>
                <Typography variant="body1" fontWeight="bold" color="primary.main">
                  {stats?.statistics.recentCompletions || 0}
                  {t('reminders.items')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* 期限間近の受講一覧 */}
      {stats?.upcomingDeadlines && stats.upcomingDeadlines.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardHeader
            title={t('reminders.upcomingDeadlinesList')}
            titleTypographyProps={{ variant: 'subtitle1' }}
          />
          <CardContent sx={{ maxHeight: 300, overflow: 'auto', pt: 0 }}>
            <List dense>
              {stats.upcomingDeadlines.map((deadline, index) => (
                <React.Fragment key={deadline.id}>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ScheduleIcon
                        fontSize="small"
                        color={deadline.daysLeft && deadline.daysLeft <= 1 ? 'error' : 'secondary'}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={deadline.test.title}
                      secondary={`${deadline.user.name} (${deadline.user.department}) - ${t('reminders.daysLeftLabel', { days: deadline.daysLeft })}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip
                      label={t('reminders.daysLeftLabel', { days: deadline.daysLeft })}
                      color={deadline.daysLeft && deadline.daysLeft <= 1 ? 'error' : 'secondary'}
                      size="small"
                    />
                  </ListItem>
                  {index < stats.upcomingDeadlines.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}
